import React, { useState, useEffect } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Switch,
  TextField,
  Typography,
  Autocomplete,
  Chip
} from "@mui/material";
import bkstApi from "../../common/component/api";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import PricingVariant from "../component/PricingVariant";
import ProductRules from "../component/ProductRules";
import { ExpandMoreOutlined } from "@mui/icons-material";
import QuillRTE from "../../common/component/QuillRTE";
import moment from "moment";
import ProductFieldOption from "../component/ProductFieldOption";
import { capitalize, getVariantOptionSurchargeForDisplay } from "../../common/component/util";
import AddField from "../component/AddField";
import { CATEGORIES } from "../../common/component/constants";
import AddIcon from "@mui/icons-material/Add";
import Images from "../../common/component/Images";
import CatalogSizeVariantGrid from "../../baker/component/CatalogSizeVariantGrid";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

const DEFAULT_CHANNEL = { pos: { status: "ACTIVE" }, online: { status: "ACTIVE" } };

export default function ProductEntry(props) {
  const { siteId, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState("");

  const [category, setCategory] = useState([]);
  const [images, setImages] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [variants, setVariants] = useState("");
  const [rules, setRules] = useState("");
  const [channel, setChannel] = useState(DEFAULT_CHANNEL);
  const [status, setStatus] = useState("ACTIVE");

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (productId) {
      setLoading(true);
      bkstApi(`/admin/site/${siteId}/product/${productId}`).then((res) => {
        setProduct(res.data);

        setCategory(res.data.category || [res.data.type]);

        setImages(res.data.images);
        setTitle(res.data.title);
        setDescription(res.data.description);

        setVariants(res.data.variants);

        setRules(res.data.rules);
        setChannel(res.data.channel || DEFAULT_CHANNEL);

        setStatus(res.data.status);

        setLoading(false);
      });
    } else {
      if (location.state) {
        const p = location.state.product;
        setProduct(p);

        setCategory(p.category || [p.type]);
        setImages(p.images);
        setTitle(p.title);
        setDescription(p.description);

        setVariants(p.variants);
        setChannel(p.channel || DEFAULT_CHANNEL);

        setRules(p.rules);
        setStatus(p.status);
      }
      setLoading(false);
    }
  }, [productId]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // validate
    let arr = [];
    if (status === "ACTIVE") {
      if (variants) {
        for (const [k, v] of Object.entries(variants)) {
          if (v.field.type === "select" && v.field.options.length === 0) {
            arr.push(`${capitalize(v.name)} has no options`);
          }
        }
        if (variants.size) {
          if (variants.size.field.options.length === 0) {
            arr.push(`Pricing required`);
          }
        } else {
          arr.push(`Pricing required`);
        }
      } else {
        arr.push(`Pricing required`);
      }

      if (rules?.date?.max) {
        if (moment().isAfter(moment(rules?.date?.max), "day")) {
          arr.push("Check Advanced Options. Max date can't be in the past");
        }

        if (rules?.date?.min && moment(rules.date.min).isAfter(moment(rules?.date?.max), "day")) {
          arr.push("Check Advanced Options. Min date can't be after Max date");
        }
      }

      if (channel && typeof channel === "object") {
        const hasActiveChannel = Object.values(channel).some((ch) => ch.status === "ACTIVE");
        if (!hasActiveChannel) {
          arr.push("At least one channel must be ACTIVE");
        }
      }
    }

    setErrors(arr);
    if (arr.length > 0) {
      return;
    }

    const payload = {
      category,
      images,
      title,
      description,
      variants,
      rules,
      channel,
      status
    };

    if (productId) {
      bkstApi.put(`/my/product/${productId}`, payload).then((res) => {
        navigate("/shop");
      });
    } else {
      bkstApi.post(`/my/product`, payload).then((res) => {
        navigate("/shop");
      });
    }
    localStorage.removeItem("products");
  };

  const sortVariants = ([k1, v1], [k2, v2]) => {
    return v2.rank - v1.rank || k2.localeCompare(k1);
  };

  const sortBySurcharge = (o1, o2) => {
    const amt1 = o1.pricing?.amount || o1.pricing?.factor || 0;
    const amt2 = o2.pricing?.amount || o2.pricing?.factor || 0;
    let rslt = +amt1 - +amt2;
    if (rslt === 0) {
      rslt = o1.name.localeCompare(o2.name);
    }

    return rslt;
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth="lg">
        <Box>
          <Box mt={6} mb={3}>
            <Typography variant="h3" gutterBottom>
              Item Details
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Add title, description, photos. Add multiple size / price variants. Create custom fields like flavor, add surcharge to premium options.
              Control availability by channel.
            </Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            <Paper elevation={0}>
              <Box px={2} pt={4} pb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Images edit={true} showArrows={true} pics={images} onChange={(arr) => setImages(arr)} folder={`img/site/${siteId}/tmp`} />
                      </Grid>
                      <Grid item xs={8}>
                        <Box>
                          <TextField
                            id="title"
                            value={title}
                            label="Title"
                            name="title"
                            variant="outlined"
                            fullWidth
                            required
                            onChange={(e) => setTitle(e.target.value)}
                            inputProps={{ maxLength: 40 }}
                          />
                        </Box>
                        <Box my={3}>
                          <Autocomplete
                            value={category}
                            getOptionDisabled={() => category.length >= 3}
                            onChange={(e, val) => {
                              setCategory(val.splice(0, 3));
                            }}
                            options={CATEGORIES}
                            filterSelectedOptions
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                {...params}
                                label="Category"
                                required={category.length === 0}
                                helperText="Choose item type (e.g. Cakes), occasion (e.g. Thanksgiving) or dietary (e.g. Vegan) to show item in selected categories. Recommened: 1. Max: 3."
                              />
                            )}
                            multiple
                            freeSolo
                            clearOnBlur={true}
                          />
                        </Box>
                        <QuillRTE value={description} onChange={(html) => setDescription(html)} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
            <Box my={4}>
              <PricingVariant
                value={variants?.size?.field?.options.sort(sortBySurcharge) || []}
                onChange={(data) => {
                  let v = { ...variants };
                  let size = v?.["size"] || { name: "Size", description: "", rank: 10, required: true, field: { type: "select", options: [] } };
                  size.field.options = data;
                  v["size"] = size;
                  setVariants(v);
                }}
              />
            </Box>
            <Box my={4}>
              <Box>
                <Grid container alignItems="center">
                  <Grid item xs={9}>
                    <Typography variant="h6">Fields</Typography>
                  </Grid>
                  <Grid item xs={3} align="right">
                    <AddField
                      disabled={Object.keys(variants)}
                      onChange={(variantField) => {
                        let o = { ...variants };
                        let k = variantField.name.toLowerCase().replace(/\s+/g, "-");
                        o[k] = product?.variants?.[k] || variantField;
                        setVariants(o);
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Paper elevation={0}>
                <Box px={2} pt={3} pb={2}>
                  {Object.keys(variants).length === 0 && (
                    <Box px={2} py={4} textAlign="center">
                      <Typography variant="subtitle1">Add Options</Typography>
                      <Typography variant="body1" color="textSecondary">
                        Click + to add options like flavor, filling, etc
                      </Typography>
                    </Box>
                  )}
                  <Grid container spacing={2}>
                    {Object.entries(variants)
                      .sort(sortVariants)
                      .map(([key, value]) => {
                        if (key === "size" || key === "add-on") {
                          return <></>;
                        } else {
                          return (
                            <Grid item xs={12}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={Boolean(variants?.[key])}
                                      onChange={(e) => {
                                        let o = { ...variants };
                                        if (e.target.checked) {
                                          o[key] = product?.variants?.[key] || [];
                                        } else {
                                          delete o[key];
                                        }
                                        setVariants(o);
                                      }}
                                    />
                                  }
                                  label={value?.name?.toUpperCase()}
                                />
                                {value.field?.type === "select" && (
                                  <ProductFieldOption
                                    category={capitalize(key)}
                                    mode="Add"
                                    value={""}
                                    onChange={(data) => {
                                      let v = { ...variants };
                                      const options = [...variants?.[key]?.field?.options];
                                      options.push(data);
                                      v[key].field.options = options;
                                      setVariants(v);
                                    }}
                                  >
                                    <Button size="small" color="primary" variant="outlined" startIcon={<AddIcon />}>
                                      OPTION
                                    </Button>
                                  </ProductFieldOption>
                                )}
                              </Box>
                              {value?.field?.type === "select" && (
                                <Box my={2}>
                                  {value?.field?.options.length == 0 && (
                                    <Alert severity="info">Click the + OPTION button above to add {value.name} options</Alert>
                                  )}
                                  {value?.field?.options?.sort(sortBySurcharge).map((d, idx) => {
                                    const surcharge = getVariantOptionSurchargeForDisplay(d);

                                    return (
                                      <ProductFieldOption
                                        category={capitalize(key)}
                                        value={d}
                                        onChange={(data) => {
                                          let v = { ...variants };
                                          const options = [...value?.field?.options];
                                          if (data) {
                                            options[idx] = data;
                                          } else {
                                            options.splice(idx, 1);
                                          }
                                          v[key].field.options = options;
                                          setVariants(v);
                                        }}
                                      >
                                        <Chip label={`${d.name}${surcharge ? ` | ${surcharge}` : ""}`} clickable />
                                      </ProductFieldOption>
                                    );
                                  })}
                                  {/* <Box mt={2}>
                                    <CatalogSizeVariantGrid
                                      catalog={{ cake: { variants } }}
                                      productType={"cake"}
                                      variantType={key}
                                      sizeChart={variants.size.field.options}
                                      options={value?.field?.options}
                                      handleSubmit={(payload) => console.log(">>>>>>>>", payload)}
                                    />
                                  </Box> */}
                                </Box>
                              )}
                              {value?.field?.type === "text" && (
                                <Box my={1}>
                                  <Typography variant="body1" color="textSecondary">
                                    Allow customers to type something
                                  </Typography>
                                </Box>
                              )}
                              {value?.field?.type === "photo" && (
                                <Box my={1}>
                                  <Typography variant="body1" color="textSecondary">
                                    Allow customers to upload photos
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
                          );
                        }
                      })}
                  </Grid>
                </Box>
              </Paper>
            </Box>
            <Box my={4}>
              <Box>
                <Grid container alignItems="center">
                  <Grid item xs={9}>
                    <Typography variant="h6">Settings</Typography>
                  </Grid>
                  <Grid item xs={3} align="right"></Grid>
                </Grid>
              </Box>
              <Paper elevation={0}>
                <Box px={2} pt={3} pb={2}>
                  <Box>
                    <Box>
                      <Typography variant="h6">Availability</Typography>
                    </Box>
                    <FormControlLabel
                      control={<Switch checked={status === "ACTIVE"} onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "DRAFT")} />}
                      label="Item is available!"
                    />
                  </Box>
                  {status === "ACTIVE" && (
                    <Box py={3}>
                      <Divider />
                      <Box py={2}>
                        <Typography variant="h6">Channel</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Set Item availability by channel
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            sx={{ gap: "10px" }}
                            control={
                              <Switch
                                size="small"
                                checked={channel.pos.status === "ACTIVE"}
                                onChange={(e) => setChannel({ ...channel, pos: { status: e.target.checked ? "ACTIVE" : "DRAFT" } })}
                              />
                            }
                            label="POS"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            sx={{ gap: "10px" }}
                            control={
                              <Switch
                                size="small"
                                checked={channel.online.status === "ACTIVE"}
                                onChange={(e) => setChannel({ ...channel, online: { status: e.target.checked ? "ACTIVE" : "DRAFT" } })}
                              />
                            }
                            label="Online Store"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  {status === "ACTIVE" && (
                    <Box>
                      <Divider />
                      <Accordion elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                          <Box>
                            <Typography variant="h6">Advanced Options</Typography>
                            <Typography variant="body2" color="textSecondary">
                              Limit availability by date range & lead time
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ marginLeft: "15px", borderLeft: "3px solid #EEE" }}>
                          <ProductRules value={rules} onChange={(data) => setRules(data)} />
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
            <Box my={4}>
              {errors?.length > 0 && (
                <Box my={2}>
                  <Alert severity="error">{errors.join(". ")}</Alert>
                </Box>
              )}
              <Box my={2} sx={{ display: "flex", gap: "15px" }}>
                <Button fullWidth variant="outlined" color="secondary" onClick={() => navigate(`/shop`)}>
                  Cancel
                </Button>
                <Button disabled={true} type="submit" variant="contained" color="primary" fullWidth>
                  Save Item
                </Button>
              </Box>
            </Box>
          </form>
          <Box>
            {Object.entries(product.variants)
              .sort(([, a], [, b]) => (b.rank ?? 0) - (a.rank ?? 0))
              .map(([variantKey, variantData]) => (
                <Box key={variantKey}>
                  <Accordion elevation={0} sx={{ border: "1px solid #DEDEDE" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle1">{variantData.name}</Typography>
                          <Typography variant="overline" color="text.secondary">
                            • {variantData.field?.type} ({variantData.field.min}-{variantData.field.max}) • {variantData.field?.options?.length}{" "}
                            options
                          </Typography>
                        </Box>
                        {variantData.required && <Chip label="Required" size="small" variant="outlined" color="secondary" />}
                        <Chip label={`Rank: ${variantData.rank}`} size="small" variant="outlined" color="secondary" />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <CatalogSizeVariantGrid
                        catalog={product}
                        productType={product.type}
                        variantType={variantKey}
                        sizeChart={product.variants.size.field.options}
                        options={variantData.field?.options}
                        handleSubmit={(payload) => {}}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              ))}

            <pre style={{ fontSize: "10px", color: "#FFF", backgroundColor: "#000", textAlign: "left" }}>{JSON.stringify(product, null, 2)}</pre>
            <Button
              disableElevation
              variant="text"
              color="secondary"
              fullWidth
              target="_blank"
              href={`https://console.firebase.google.com/project/bakestreet-f55a0/firestore/databases/-default-/data/~2Fbaker~2F${siteId}~2Fproduct~2F${productId}`}
            >
              View in DB
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }
}
